/**
 * Used only to pass data to the machine initialization.
 */
import { useMemo } from 'react'

import { useExtensionData } from '$extensionSrc/hooks/extensionStorageListener'
import { getRecommendationKeysForPlatform } from '$extensionSrc/recommendations/recommendationRegistryApi'
import {
  isValidRecommendationKey,
  RecommendationKeyEnum,
} from '$extensionSrc/recommendations/recommendationTypes'
import { PLATFORM, STORAGE_KEY } from '$extensionSrc/utils/enums'
import * as localStorage from '$extensionSrc/utils/localStorage'

export type BatchUpdateRecommendationKeysBlob = Partial<
  Record<PLATFORM, RecommendationKeyEnum[]>
>

export function parseBatchUpdateRecommendationKeysBlob(
  blob: any,
): BatchUpdateRecommendationKeysBlob {
  if (!blob || typeof blob !== 'object') {
    return {}
  }

  const result: BatchUpdateRecommendationKeysBlob = {}

  for (const [key, value] of Object.entries(blob)) {
    if (
      // Validate that key is a valid PLATFORM; if not, skip key
      key in PLATFORM &&
      // Validate that value is an array; if not, skip key
      Array.isArray(value) &&
      // Validate that all items in array are valid RecommendationKeyEnum values
      value.every(
        (item) => typeof item === 'string' && isValidRecommendationKey(item),
      )
    ) {
      result[key as PLATFORM] = value as RecommendationKeyEnum[]
    }
  }

  return result
}

export async function getBlob(): Promise<BatchUpdateRecommendationKeysBlob> {
  const blob = await localStorage.getSingle(
    STORAGE_KEY.BATCH_UPDATE_RECOMMENDATION_KEYS,
  )
  return parseBatchUpdateRecommendationKeysBlob(blob)
}

export async function getBatchUpdateRecommendationKeys(
  platform: PLATFORM,
): Promise<RecommendationKeyEnum[]> {
  const blob = await getBlob()

  const batchUpdateKeysForPlatform = blob?.[platform]
  const platformRecommendationKeys = getRecommendationKeysForPlatform(platform)

  if (platformRecommendationKeys && batchUpdateKeysForPlatform) {
    return platformRecommendationKeys.filter((key) =>
      batchUpdateKeysForPlatform.includes(key),
    )
  }

  return []
}

export async function setBatchUpdateRecommendationKeys(
  platform: PLATFORM,
  batchUpdateRecommendationKeys: RecommendationKeyEnum[],
): Promise<void> {
  const blob = await getBlob()

  return localStorage.set({
    [STORAGE_KEY.BATCH_UPDATE_RECOMMENDATION_KEYS]: {
      ...blob,
      [platform]: batchUpdateRecommendationKeys,
    },
  })
}

export async function clearBatchUpdateRecommendationKeys(
  platform: PLATFORM,
): Promise<void> {
  const blob = await getBlob()

  delete blob[platform]

  return localStorage.set({
    [STORAGE_KEY.BATCH_UPDATE_RECOMMENDATION_KEYS]: blob,
  })
}

export function useBatchUpdateRecommendationKeys(
  platform: PLATFORM,
): RecommendationKeyEnum[] | undefined {
  const { data, isLoaded } = useExtensionData({
    local: [STORAGE_KEY.BATCH_UPDATE_RECOMMENDATION_KEYS],
  })

  return useMemo(() => {
    if (!isLoaded || !data) {
      return undefined
    }

    const blob = parseBatchUpdateRecommendationKeysBlob(
      data[STORAGE_KEY.BATCH_UPDATE_RECOMMENDATION_KEYS],
    )
    return blob?.[platform] || []
  }, [platform, isLoaded, data])
}
